import {Component, DestroyRef, OnInit, computed, inject, signal} from '@angular/core';
import {takeUntilDestroyed, toObservable} from '@angular/core/rxjs-interop';
import {CommonModule} from '@angular/common';
import {NgxChartsModule, Color, ScaleType} from '@swimlane/ngx-charts';
import {forkJoin} from 'rxjs';
import {TrendGranularity, TrendPoint} from '../../models/dashboard-statistics.model';
import {DashboardStatisticsService} from '../../services/dashboard-statistics.service';
import {DashboardPeriodService} from '../../../dashboard/services/dashboard-period.service';

@Component({
  selector: 'app-activity-trends',
  standalone: true,
  imports: [CommonModule, NgxChartsModule],
  templateUrl: './activity-trends.component.html',
  styleUrls: ['./activity-trends.component.css']
})
export class ActivityTrendsComponent implements OnInit {

  private readonly statsService = inject(DashboardStatisticsService);
  private readonly periodService = inject(DashboardPeriodService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly range$ = toObservable(this.periodService.currentRange);

  granularity = this.periodService.trendGranularity;
  isLoading = signal<boolean>(true);

  private importsTrendSignal = signal<TrendPoint[]>([]);
  private pureOriginalTrendSignal = signal<TrendPoint[]>([]);
  private masterTrendSignal = signal<TrendPoint[]>([]);

  chartData = computed(() => [
    {
      name: 'Imports',
      series: this.importsTrendSignal().map(p => ({name: p.period, value: p.count}))
    },
    {
      name: 'Pure Originals',
      series: this.pureOriginalTrendSignal().map(p => ({name: p.period, value: p.count}))
    },
    {
      name: 'Masters',
      series: this.masterTrendSignal().map(p => ({name: p.period, value: p.count}))
    }
  ]);

  colorScheme: Color = {
    name: 'custom',
    selectable: true,
    group: ScaleType.Ordinal,
    domain: ['#7770CD', '#5AA155', '#E8A838']
  };

  formatXAxisTick = (val: string): string => {
    if (!val) return val;
    const parts = val.split('-');
    if (parts.length >= 3) {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthIdx = parseInt(parts[1], 10) - 1;
      return `${months[monthIdx]} ${parseInt(parts[2], 10)}`;
    }
    return val;
  };

  legendItems = [
    {label: 'Imports', color: '#7770CD'},
    {label: 'Pure Originals', color: '#5AA155'},
    {label: 'Masters', color: '#E8A838'}
  ];

  ngOnInit(): void {
    this.range$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.loadData();
    });
  }

  onGranularityChange(g: TrendGranularity): void {
    this.granularity.set(g);
    this.loadData();
  }

  private loadData(): void {
    const start = this.periodService.currentStart();
    const end = this.periodService.currentEnd();
    const gran = this.granularity();
    const poRange = this.periodService.pureOriginalRange();
    const masterRange = this.periodService.masterRange();
    this.isLoading.set(true);
    forkJoin({
      imports: this.statsService.getImportsTrend(start, end, gran),
      po: this.statsService.getPureOriginalTrend(poRange.start, poRange.end, gran),
      master: this.statsService.getMasterTrend(masterRange.start, masterRange.end, gran)
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: results => {
          this.importsTrendSignal.set(results.imports);
          this.pureOriginalTrendSignal.set(results.po);
          this.masterTrendSignal.set(results.master);
          this.isLoading.set(false);
        },
        error: () => this.isLoading.set(false)
      });
  }
}
